import { Button } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Space, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import React from "react";
import { useParams } from "react-router-dom";
import { Title } from "../../components/Desc";
import ProgressIndicator from "../../components/ProgressIndicator";
import strings from "../../config/localization";
import {
  useGetTimeslotPersonnelDetailsQuery,
  useGetTimeslotsQuery,
} from "../../services/appln";
import { getLocationKinds, getLocationStrMap } from "../util";

export default function PersonnelReport() {
  const params = useParams();

  const [timeslotMap, setTimeslotMap] = React.useState<any>(
    new Map<string, string>(),
  );
  const [selectedTimeslot, setSelectedTimeslot] = React.useState<any>("");

  const [selectedLocationKinds, setSelectedLocationKinds] = React.useState<any>(
    [strings.area, strings.point, strings.range, strings.subarea],
  );
  const [designationNames, setDesignationNames] = React.useState<any>([]);

  const [showAssessmentCount, setShowAssessmentCount] = React.useState(true);
  const [showAllocationCount, setShowAllocationCount] = React.useState(true);
  const [showAllocationPending, setShowAllocationPending] =
    React.useState(true);

  // selected designations
  const [selectedDesignations, setSelectedDesignations] = React.useState<
    Set<string>
  >(new Set<string>());

  const [rawData, setRawData] = React.useState<any>([]);

  interface LocationInfo {
    id: string;
    name: string;
    kind: string;
    details: any;
  }

  const detailsQueryResponse = useGetTimeslotPersonnelDetailsQuery({
    bandobastId: params.bandobastId,
    timeslotId: timeslotMap.get(selectedTimeslot)!,
  });

  React.useEffect(() => {
    if (!detailsQueryResponse.isSuccess) {
      return;
    }
    setRawData(detailsQueryResponse.data.data);
  }, [detailsQueryResponse]);

  const timeslotsQueryResponse = useGetTimeslotsQuery(params.bandobastId);
  React.useEffect(() => {
    if (!timeslotsQueryResponse.isSuccess) {
      return;
    }
    const timeslotMap = new Map<string, string>();
    timeslotsQueryResponse.data?.data.forEach((element: any) => {
      timeslotMap.set(element.name, element.id);
    });
    setTimeslotMap(timeslotMap);
  }, [timeslotsQueryResponse]);

  if (
    detailsQueryResponse.isLoading ||
    detailsQueryResponse.isFetching ||
    timeslotsQueryResponse.isLoading ||
    timeslotsQueryResponse.isFetching
  ) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          width: "100%",
          marginTop: "10px",
          marginLeft: "20px",
          marginRight: "20px",
        }}
      >
        <Title text={strings.personnelAllocation}></Title>
        <ProgressIndicator />
      </Box>
    );
  }

  console.log("SummaryReport, rawData", rawData);

  const columns: ColumnsType<LocationInfo> = [
    {
      title: strings.name,
      dataIndex: "name",
      key: "name",
    },
    {
      title: strings.kind,
      dataIndex: "kind",
      key: "kind",
      render: (text, record, index) => getLocationStrMap().get(text)!,
    },
  ];

  designationNames.forEach((name: string) => {
    // add designation column only if its selected
    if (selectedDesignations.has(name)) {
      columns.push({
        title: name,
        dataIndex: name,
        key: name,
        render: (text, record, index) => {
          // console.log("text, record, index", text, record, index);
          const row: any = record["details"].find((e: any) => {
            if (e.abbreviation !== "") {
              return e.abbreviation === name;
            }
            return e.designationName === name;
          });
          // console.log("row", row);
          // console.log("name", name);
          return (
            <Space direction="horizontal">
              {showAssessmentCount && (
                <Tag color="magenta">{row["assessmentCount"]}</Tag>
              )}
              {showAllocationCount && (
                <Tag color="cyan">{row["allocationCount"]}</Tag>
              )}
              {showAllocationPending && (
                <Tag color="blue">{row["allocationPendingCount"]}</Tag>
              )}
            </Space>
          );
        },
      });
    }
  });

  return (
    <div>
      <Box
        sx={{
          height: 400,
          width: "100%",
          marginTop: "10px",
          marginLeft: "20px",
          marginRight: "20px",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Title text={strings.personnelAssessmentAllocationSummary}></Title>
          </Grid>
          <Grid item xs={7}>
            <Autocomplete
              options={Array.from(timeslotMap.keys())}
              value={selectedTimeslot}
              onChange={(e, value) => {
                setSelectedTimeslot(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  variant="standard"
                  label={strings.timeslot}
                />
              )}
            />
          </Grid>
          <Grid item xs={7}>
            <Autocomplete
              multiple
              options={getLocationKinds()}
              value={selectedLocationKinds}
              onChange={(e, value) => {
                console.log("selected location kinds", value);
                setSelectedLocationKinds(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  variant="standard"
                  label={strings.kind}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <FormGroup row={true}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showAssessmentCount}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setShowAssessmentCount(event.target.checked);
                    }}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label={strings.showAssessmentCount}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showAllocationCount}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setShowAllocationCount(event.target.checked);
                    }}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label={strings.showAllocationCount}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showAllocationPending}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setShowAllocationPending(event.target.checked);
                    }}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label={strings.showAllocationPending}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={12}>
            <FormGroup row={true}>
              {designationNames.map((name: string) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedDesignations.has(name)}
                      color="secondary"
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        // console.log(
                        //   "name:",
                        //   name,
                        //   "event.target.checked:",
                        //   event.target.checked,
                        // );
                        if (event.target.checked) {
                          selectedDesignations.add(name);
                        } else {
                          selectedDesignations.delete(name);
                        }
                        // create a copy since updating existing copy does not refresh the ui
                        const newSelectedDesignations = new Set<string>([
                          ...selectedDesignations,
                        ]);
                        // console.log("newSelectedDesignations:", [
                        //   ...newSelectedDesignations,
                        // ]);
                        setSelectedDesignations(newSelectedDesignations);
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  }
                  label={name}
                />
              ))}
            </FormGroup>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={() => {
                console.log("in export handler");
              }}
            >
              {strings.export}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
}
